import React from "react";
import { Box, Typography, Container } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import ThemeToggle from "./ThemeToggle";

const BRAND = { navy: "#00357a", navyDark: "#001f4d", gold: "#c9a96e" };

const SECTIONS = [
  {
    title: "1. Introduction",
    body: `Labarium Diagnostic Systems, Inc. ("Labarium," "we," "us," or "our") is committed to protecting the privacy and security of the personal and health information entrusted to us. This Privacy Policy explains how we collect, use, disclose, and safeguard information when you use the Labarium AI platform (the "Service").\n\nWe operate in full compliance with the Health Insurance Portability and Accountability Act of 1996 (HIPAA) and its implementing regulations, as well as applicable state privacy laws.`,
  },
  {
    title: "2. Information We Collect",
    body: `We collect the following categories of information:\n\n• Account Information: Your name, email address, password (stored as a bcrypt hash), role (patient or clinician), and — for clinicians — practice name and National Provider Identifier (NPI).\n\n• Lab Report Content: Images and text extracted from the laboratory reports you upload. We process this information solely to provide the AI analysis you request.\n\n• Protected Health Information (PHI): Patient names and dates of birth extracted from reports are stored only as one-way SHA-256 cryptographic hashes. These hashed values cannot be reverse-engineered to recover the original data. Other PHI fields (collection date, age, sex) are stored in encrypted form using AES-128 Fernet encryption.\n\n• Analysis History: Summaries of prior analyses are stored in our secure database to enable longitudinal trend detection when you enable the Long-term Memory feature.\n\n• Usage Data: Log data including IP addresses, browser type, pages visited, and timestamps, collected for security, performance monitoring, and abuse prevention.`,
  },
  {
    title: "3. How We Use Your Information",
    body: `We use the information we collect to:\n\n• Provide, operate, and improve the Service.\n• Generate AI-powered analysis of your laboratory reports.\n• Detect longitudinal trends across multiple lab reports when you enable memory features.\n• Authenticate your identity and maintain account security.\n• Communicate with you about your account, updates, and support.\n• Comply with legal obligations, including HIPAA requirements.\n• Prevent fraud, abuse, and unauthorized access.\n\nWe do not sell your personal information or PHI to third parties. We do not use your health information for advertising or marketing purposes.`,
  },
  {
    title: "4. AI Processing and Third-Party AI Providers",
    body: `Our AI analysis features are powered in part by:\n\n• Google Gemini (via Google AI Studio API): Used for clinical analysis and summary generation. We transmit only cropped images of test result sections — never the patient name, date of birth, or identifying header information from your reports.\n\n• Ollama (local processing): Used for PHI extraction (patient name and date of birth) from report headers. This processing occurs locally on our servers and extracted identifiers are immediately hashed and discarded.\n\nBy using the Service and uploading lab reports, you consent to the processing described above. We have data processing agreements in place with our AI providers that prohibit them from using your data to train their models.`,
  },
  {
    title: "5. Data Storage and Security",
    body: `Your data is stored on secured cloud infrastructure protected by:\n\n• TLS 1.2+ encryption for all data in transit.\n• AES-128 Fernet encryption for PHI fields stored at rest.\n• SHA-256 one-way hashing for patient name and date of birth.\n• Role-based access controls limiting data access to authorized personnel only.\n• MongoDB Atlas with IP allowlisting and authentication.\n\nWe maintain a comprehensive security program aligned with HIPAA Security Rule requirements, including regular risk assessments and employee training.`,
  },
  {
    title: "6. Data Retention",
    body: `We retain your account information for as long as your account is active or as necessary to provide the Service. Analysis history and stored summaries are retained to support longitudinal trend detection and may be deleted by you at any time using the "Clear All Memory" function within the application.\n\nFollowing account termination, we retain data for a minimum period as required by applicable law and our HIPAA obligations, after which it is securely deleted.`,
  },
  {
    title: "7. HIPAA Business Associate Agreements",
    body: `If you are a covered entity under HIPAA or a business associate using the Service in connection with PHI, please contact us at www.labarium.com to execute a Business Associate Agreement (BAA) before using the Service. Labarium will serve as a Business Associate for covered entities and will comply with the HIPAA requirements applicable to business associates.`,
  },
  {
    title: "8. Cookies and Tracking Technologies",
    body: `We use session cookies and local storage to maintain your authenticated session. We do not use cross-site tracking cookies or third-party advertising trackers. Usage analytics may be collected for performance monitoring purposes. You can disable cookies in your browser settings, but this may impact the functionality of the Service.`,
  },
  {
    title: "9. Your Rights",
    body: `Subject to applicable law, you have the following rights regarding your personal information:\n\n• Access: You may request a copy of the personal information we hold about you.\n• Correction: You may request correction of inaccurate personal information.\n• Deletion: You may request deletion of your account and associated data. Note that some data may be retained as required by law.\n• Data Portability: You may request a machine-readable export of your data.\n• Restriction: You may request that we restrict processing of your data in certain circumstances.\n\nTo exercise any of these rights, please contact us through our website at www.labarium.com.`,
  },
  {
    title: "10. Children's Privacy",
    body: `The Service is not directed to children under the age of 18. We do not knowingly collect personal information from children under 18. If you believe we have inadvertently collected information from a child under 18, please contact us immediately and we will promptly delete such information.`,
  },
  {
    title: "11. Sharing of Information",
    body: `We do not sell, rent, or trade your personal information. We may share information in the following limited circumstances:\n\n• Service Providers: We share information with trusted service providers (cloud hosting, payment processing, AI API providers) who process data on our behalf under strict data processing agreements.\n\n• Legal Requirements: We may disclose information when required by law, court order, or government authority, or to protect the rights, property, or safety of Labarium, our users, or the public.\n\n• Business Transfers: In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction, subject to equivalent privacy protections.\n\n• With Your Consent: We may share information for any other purpose with your explicit consent.`,
  },
  {
    title: "12. Changes to This Policy",
    body: `We may update this Privacy Policy from time to time. Material changes will be communicated via email and a prominent notice within the Service at least 14 days before taking effect. Your continued use of the Service after any change becomes effective constitutes your acceptance of the updated policy.`,
  },
  {
    title: "13. Contact Us",
    body: `If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:\n\nLabarium Diagnostic Systems, Inc.\nwww.labarium.com\n\nFor HIPAA-related matters or to report a potential privacy incident, please contact us through our website as promptly as possible.`,
  },
];

export default function PrivacyPolicy() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const bg = isDark ? "#080d1a" : "#f4f7ff";
  const cardBg = isDark ? "#111827" : "#ffffff";
  const textPrimary = isDark ? "#e8edf5" : "#0d1b2e";
  const textSecondary = isDark ? "#94a3b8" : "#4a5568";
  const border = isDark ? "#1e2d50" : "#e2e8f0";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: bg, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box sx={{
        bgcolor: isDark ? "rgba(2,6,15,0.95)" : "rgba(0,31,77,0.97)",
        borderBottom: "2px solid rgba(192,168,140,0.3)",
        flexShrink: 0,
      }}>
        <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, py: 1, minHeight: 64,
          display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 1 }}>
          <Box component={Link} to="/" sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
            <img src="/logo-labarium-dark.png" alt="Labarium" style={{ height: 46, width: "auto" }} />
          </Box>
          <ThemeToggle />
        </Box>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: 8, flex: 1 }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          {/* Title block */}
          <Box sx={{ mb: 6 }}>
            <Typography sx={{
              fontSize: { xs: 12, sm: 13 }, fontWeight: 700, letterSpacing: "0.12em",
              color: BRAND.gold, mb: 1.5, textTransform: "uppercase",
            }}>
              Legal
            </Typography>
            <Typography sx={{
              fontSize: { xs: 32, sm: 42 }, fontWeight: 800,
              color: textPrimary, lineHeight: 1.15, mb: 2, letterSpacing: "-0.02em",
            }}>
              Privacy Policy
            </Typography>
            <Typography sx={{ fontSize: 16, color: textSecondary, lineHeight: 1.75, maxWidth: 600 }}>
              This Privacy Policy describes how Labarium Diagnostic Systems, Inc. collects, uses, and protects your personal and health information.
            </Typography>
            <Box sx={{ mt: 3, display: "inline-flex", gap: 1.5, flexWrap: "wrap" }}>
              <Box sx={{
                fontSize: 14.5, color: textSecondary,
                bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,53,122,0.05)",
                border: `1px solid ${border}`, borderRadius: "8px", px: 2, py: 0.75,
              }}>
                Effective date: May 1, 2025
              </Box>
              <Box sx={{
                fontSize: 14.5, color: textSecondary,
                bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,53,122,0.05)",
                border: `1px solid ${border}`, borderRadius: "8px", px: 2, py: 0.75,
              }}>
                Last updated: May 25, 2026
              </Box>
              <Box sx={{
                fontSize: 14.5, fontWeight: 700, color: "#16a34a",
                bgcolor: "rgba(34,197,94,0.08)",
                border: "1px solid rgba(34,197,94,0.25)", borderRadius: "8px", px: 2, py: 0.75,
              }}>
                HIPAA Compliant
              </Box>
            </Box>
          </Box>

          {/* Sections */}
          <Box sx={{
            bgcolor: cardBg,
            border: `1px solid ${border}`,
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: isDark ? "0 8px 40px rgba(0,0,0,0.35)" : "0 8px 40px rgba(0,53,122,0.08)",
          }}>
            {SECTIONS.map((section, idx) => (
              <Box key={idx} sx={{
                px: { xs: 3, sm: 5 }, py: 3.5,
                borderBottom: idx < SECTIONS.length - 1 ? `1px solid ${border}` : "none",
              }}>
                <Typography sx={{
                  fontSize: 17, fontWeight: 700, color: isDark ? "#f1f5f9" : BRAND.navy,
                  mb: 1.5, lineHeight: 1.35,
                }}>
                  {section.title}
                </Typography>
                {section.body.split("\n\n").map((para, i) => (
                  <Typography key={i} component="p" sx={{
                    fontSize: 15, color: textSecondary, lineHeight: 1.85, mb: 1.5,
                    "&:last-child": { mb: 0 },
                    whiteSpace: "pre-line",
                  }}>
                    {para}
                  </Typography>
                ))}
              </Box>
            ))}
          </Box>

          {/* Footer nav */}
          <Box sx={{ mt: 6, display: "flex", gap: 3, flexWrap: "wrap", justifyContent: "center" }}>
            <Box component={Link} to="/terms"
              sx={{ fontSize: 14, color: BRAND.gold, textDecoration: "underline", fontWeight: 600 }}>
              Terms of Use
            </Box>
            <Box component={Link} to="/"
              sx={{ fontSize: 14, color: textSecondary, textDecoration: "none",
                "&:hover": { color: BRAND.navy } }}>
              ← Back to Sign In
            </Box>
          </Box>
        </motion.div>
      </Container>

      {/* Footer */}
      <Box sx={{
        bgcolor: isDark ? "rgba(2,6,15,0.9)" : BRAND.navyDark,
        py: 2.5, textAlign: "center",
        borderTop: "1px solid rgba(255,255,255,0.06)",
      }}>
        <Typography sx={{ fontSize: 13.5, color: "rgba(255,255,255,0.3)", letterSpacing: "0.04em" }}>
          © {new Date().getFullYear()} Labarium Diagnostic Systems, Inc. All rights reserved. &nbsp;·&nbsp; www.labarium.com
        </Typography>
      </Box>
    </Box>
  );
}
